import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators, ValidatorFn } from '@angular/forms';
import { ImportConfig, ValidationResult, ValidationError, ImportColumnConfig } from '../interfaces/import-config.interface';
import { ImportError, ImportErrorCollection } from '../models/import-error.model';
import { ExcelSheet } from './excel-processor.service';
import { ErrorMessageService } from './error-message.service';

@Injectable({
  providedIn: 'root'
})
export class ImportValidationService {

  constructor(
    private fb: FormBuilder,
    private errorMessageService: ErrorMessageService
  ) {}

  createFormArray(data: any[], config: ImportConfig): FormArray {
    const formArray = this.fb.array([]);
    
    data.forEach((row, index) => {
      const rowFormGroup = this.createRowFormGroup(row, config, index);
      // @ts-ignore
      formArray.push(rowFormGroup);
    });
    
    return formArray;
  }

  private createRowFormGroup(row: any, config: ImportConfig, rowIndex: number): FormGroup {
    const formGroup = this.fb.group({});
    
    config.columns.forEach(columnConfig => {
      const validators = this.buildValidators(columnConfig, rowIndex);
      const value = row[columnConfig.key] || '';
      
      formGroup.addControl(columnConfig.key, this.fb.control(value, validators));
    });
    
    formGroup.addControl('_rowIndex', this.fb.control(rowIndex));
    formGroup.addControl('_isValid', this.fb.control(true));
    formGroup.addControl('_errors', this.fb.control({}));
    
    return formGroup;
  }

  private buildValidators(columnConfig: ImportColumnConfig, rowIndex: number): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    
    if (columnConfig.required) {
      validators.push(this.requiredValidator(columnConfig));
    }
    
    validators.push(this.dataTypeValidator(columnConfig));
    
    if (columnConfig.validation && Array.isArray(columnConfig.validation)) {
      // Wrap existing validators to provide custom error messages
      const wrappedValidators = columnConfig.validation.map(validator => {
        return this.wrapValidatorWithCustomMessage(validator, columnConfig);
      });
      validators.push(...wrappedValidators);
    }
    
    return validators;
  }

  private requiredValidator(columnConfig: ImportColumnConfig): ValidatorFn {
    return (control) => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        const message = this.errorMessageService.getErrorMessage('required', columnConfig);
        return { required: { message, errorKey: 'required' } };
      }
      return null;
    };
  }

  private dataTypeValidator(columnConfig: ImportColumnConfig): ValidatorFn {
    return (control) => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null;
      }
      
      const dataType = columnConfig.dataType;
      let errorKey = 'dataType';
      let isValid = true;
      
      switch (dataType) {
        case 'number':
          if (isNaN(Number(value))) {
            errorKey = 'invalidNumber';
            isValid = false;
          }
          break;
        case 'date':
          if (isNaN(Date.parse(value))) {
            errorKey = 'invalidDate';
            isValid = false;
          }
          break;
        case 'boolean':
          const validBooleans = ['true', 'false', '1', '0', 'yes', 'no', 'sí', 'si'];
          if (!validBooleans.includes(String(value).toLowerCase())) {
            errorKey = 'invalidBoolean';
            isValid = false;
          }
          break;
      }
      
      if (!isValid) {
        const message = this.errorMessageService.getErrorMessage(errorKey, columnConfig);
        return { [errorKey]: { message, errorKey, value } };
      }
      
      return null;
    };
  }

  private wrapValidatorWithCustomMessage(validator: ValidatorFn, columnConfig: ImportColumnConfig): ValidatorFn {
    return (control) => {
      const result = validator(control);
      if (!result) return null;

      // Transform the error to include custom message
      const wrappedResult: any = {};
      
      Object.keys(result).forEach(errorKey => {
        const originalError = result[errorKey];
        const customMessage = this.errorMessageService.getErrorMessage(
          errorKey, 
          columnConfig, 
          originalError
        );
        
        wrappedResult[errorKey] = {
          message: customMessage,
          errorKey,
          originalError,
          ...originalError
        };
      });
      
      return wrappedResult;
    };
  }

  validateSheet(sheet: ExcelSheet, config: ImportConfig): ValidationResult {
    const errors: ValidationError[] = [];
    let totalErrors = 0;
    
    sheet.processedData.forEach((row, rowIndex) => {
      const rowErrors = this.validateRow(row, config, rowIndex);
      errors.push(...rowErrors);
      totalErrors += rowErrors.length;
    });
    
    if (config.customValidator) {
      const customResult = config.customValidator(sheet.processedData);
      errors.push(...customResult.errors);
      totalErrors += customResult.errors.length;
    }
    
    return {
      isValid: totalErrors === 0,
      errors,
      totalErrors
    };
  }

  private validateRow(row: any, config: ImportConfig, rowIndex: number): ValidationError[] {
    const errors: ValidationError[] = [];
    
    config.columns.forEach(columnConfig => {
      const cellErrors = this.validateCell(
        row[columnConfig.key], 
        columnConfig, 
        rowIndex
      );
      errors.push(...cellErrors);
    });
    
    return errors;
  }

  private validateCell(value: any, columnConfig: any, rowIndex: number): ValidationError[] {
    const errors: ValidationError[] = [];
    const validators = this.buildValidators(columnConfig, rowIndex);
    
    const tempControl = this.fb.control(value);
    
    validators.forEach(validator => {
      const result = validator(tempControl);
      if (result) {
        Object.keys(result).forEach(key => {
          errors.push({
            row: rowIndex,
            column: columnConfig.key,
            message: result[key],
            severity: 'error'
          });
        });
      }
    });
    
    return errors;
  }

  updateFormArrayValidation(formArray: FormArray, config: ImportConfig): ImportErrorCollection {
    const errorCollection = new ImportErrorCollection();
    
    formArray.controls.forEach((control, rowIndex) => {
      const formGroup = control as FormGroup;
      const rowErrors = this.validateFormGroupRow(formGroup, config, rowIndex);
      errorCollection.addErrors(rowErrors);
      
      const isValid = rowErrors.length === 0;
      formGroup.get('_isValid')?.setValue(isValid);
      
      const errorMap: { [key: string]: string[] } = {};
      rowErrors.forEach(error => {
        if (!errorMap[error.column]) {
          errorMap[error.column] = [];
        }
        errorMap[error.column].push(error.message);
      });
      formGroup.get('_errors')?.setValue(errorMap);
    });
    
    return errorCollection;
  }

  private validateFormGroupRow(formGroup: FormGroup, config: ImportConfig, rowIndex: number): ImportError[] {
    const errors: ImportError[] = [];
    
    config.columns.forEach(columnConfig => {
      const control = formGroup.get(columnConfig.key);
      if (control && control.errors) {
        Object.keys(control.errors).forEach(errorKey => {
          errors.push(new ImportError(
            rowIndex,
            columnConfig.key,
            control.errors![errorKey],
            'error'
          ));
        });
      }
    });
    
    return errors;
  }

  validateFileRequirements(file: File, config: ImportConfig): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (config.maxFileSize && file.size > config.maxFileSize) {
      errors.push(`El archivo excede el tamaño máximo permitido (${this.formatFileSize(config.maxFileSize)})`);
    }
    
    if (config.acceptedFileTypes && config.acceptedFileTypes.length > 0) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const isValidType = config.acceptedFileTypes.some(type => 
        type.toLowerCase().includes(fileExtension || '')
      );
      if (!isValidType) {
        errors.push(`Tipo de archivo no permitido. Tipos aceptados: ${config.acceptedFileTypes.join(', ')}`);
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  private formatFileSize(bytes: number): string {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }

  getErrorMessagesByRow(formArray: FormArray): { [rowIndex: number]: string[] } {
    const errorsByRow: { [rowIndex: number]: string[] } = {};
    
    formArray.controls.forEach((control, rowIndex) => {
      const formGroup = control as FormGroup;
      const errors = formGroup.get('_errors')?.value || {};
      
      const rowErrors: string[] = [];
      Object.keys(errors).forEach(column => {
        rowErrors.push(...errors[column]);
      });
      
      if (rowErrors.length > 0) {
        errorsByRow[rowIndex] = rowErrors;
      }
    });
    
    return errorsByRow;
  }

  getColumnErrorCount(formArray: FormArray, columnKey: string): number {
    let count = 0;
    
    formArray.controls.forEach(control => {
      const formGroup = control as FormGroup;
      const columnControl = formGroup.get(columnKey);
      if (columnControl && columnControl.errors) {
        count++;
      }
    });
    
    return count;
  }

  getTotalErrorCount(formArray: FormArray): number {
    let count = 0;
    
    formArray.controls.forEach(control => {
      const formGroup = control as FormGroup;
      const isValid = formGroup.get('_isValid')?.value;
      if (!isValid) {
        count++;
      }
    });
    
    return count;
  }
  
  // Method to validate a single field value (used in batch validation)
  validateField(value: any, columnConfig: ImportColumnConfig, rowData?: any): { [key: string]: any } | null {
    const validators = this.buildValidators(columnConfig, 0); // rowIndex not needed for basic validation
    const tempControl = this.fb.control(value);
    
    const errors: { [key: string]: any } = {};
    let hasErrors = false;
    
    validators.forEach(validator => {
      const result = validator(tempControl);
      if (result) {
        Object.keys(result).forEach(errorKey => {
          errors[errorKey] = result[errorKey];
          hasErrors = true;
        });
      }
    });
    
    return hasErrors ? errors : null;
  }
}